import { BoxDimensions, SlipSheet_t } from 'blackbox';
import {
	ConfigSetter,
	UseStateObject,
	getChangeWrapperString,
	render_length,
	render_length_unit,
} from '../shared/shared';
import { Application, UnitsSystem } from 'libapi';
import ContentItem, {
	ButtonProps,
	ConfirmTrigger,
	ConfirmAction,
	useConfirm,
	ContentItemProps,
	ContentFooter,
} from './ContentItem';
import { Scroll, useMachineConfig } from '../General';
import { useContext, useMemo, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';
import { ConfigurationProps } from '../Configuration';
import { duplicateConfiguration, ItemIterator, SummaryItemCell, SummaryScroll } from './Shared';
import {
	PalletizerContext,
	RuntimeContext,
} from '../../context/PalletizerContext';
import { ApplicationContext } from '../../App';
import { KeyboardTrigger, SelectTrigger } from '../Layout';
import { CoordinateItem } from './Boxes';
import { BoxRenderProps } from '../three/utils';
import BoxScene from '../three/BoxScene';


interface SlipSheetProps {
	slipsheet: SlipSheet_t;
	startEdit: () => void;
	handleDelete: () => void;
  handleCopy: () => void;
	unitsSystem: UnitsSystem;
}

function SlipSheetCell({
	slipsheet,
	startEdit,
	handleDelete,
  handleCopy,
	unitsSystem,
}: SlipSheetProps) {
	const { width, length, height } = slipsheet.dimensions;
	const brp: BoxRenderProps = { width, height: height + 1, length, hasLabels: false };

	let properties = `height: ${render_length(
		height,
		unitsSystem
	)} ${render_length_unit(unitsSystem)}`;
	return (
		<SummaryItemCell
			name={slipsheet.name}
			properties={properties}
			trashAction={handleDelete}
      duplicateAction={handleCopy}
			editAction={startEdit}
		>
			<BoxScene {...brp} showFaceLabels={false} zoom={0.7} />
		</SummaryItemCell>
	);
}

interface CreateNewSlipSheetProps {
	slipsheet: SlipSheet_t;
	LeftButton: ButtonProps;
	RightButton: ButtonProps;
	updateSlipSheet: (s: SlipSheet_t) => void;
	instructionNumber: number;
	instruction: string;
	sub_step: number;
	sub_step_count: number;
	ConfirmProps: ConfirmAction | undefined;
	unitsSystem: UnitsSystem;
	triggerKeyboard: KeyboardTrigger;
	triggerSelect: SelectTrigger;
}

function CreateNewSlipSheet({
	slipsheet,
	LeftButton,
	RightButton,
	updateSlipSheet,
	instructionNumber,
	sub_step,
	sub_step_count,
	instruction,
	ConfirmProps,
	unitsSystem,
	triggerKeyboard,
	triggerSelect,
}: CreateNewSlipSheetProps) {
	const app = useContext(ApplicationContext) as Application;

	const { lMachineConfig } = useMachineConfig(app);
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;

	const updateDimension = (dim: keyof BoxDimensions) => (val: number) => {
		const dims = { ...slipsheet.dimensions };
		dims[dim] = val;
		updateSlipSheet({ ...slipsheet, dimensions: dims });
	};
	const updateRigid = (pRigid: boolean) => {
		updateSlipSheet({ ...slipsheet, rigid: pRigid });
	};
	const lNameHandlers = getChangeWrapperString(function (pNewName: string) {
		updateSlipSheet({ ...slipsheet, name: pNewName });
	});
	const lTriggerKeyboardName = function () {
		if (triggerKeyboard) {
			triggerKeyboard(
				'Enter a name for your Slip Sheet:',
				slipsheet.name,
				lNameHandlers.Direct
			);
		}
	};

	const contentItemProps: ContentItemProps = {
		instruction,
		RightButton,
		LeftButton,
		instructionNumber,
		sub_step,
		sub_step_count,
		confirmAction: ConfirmProps,
	};

	const adjustedDim = { ...slipsheet.dimensions };
	adjustedDim.height += 1;
	const brp: BoxRenderProps = {
		length: adjustedDim.length,
		width: adjustedDim.width,
		height: adjustedDim.height,
    hasLabels: false
	};
	return (
		<ContentItem {...contentItemProps}>
			<div className="full-size flex-center align-start color-blue">
				<div className="grid-col-70 flex-between full-height full-width">
					<div className="BoxConfigurator">
						<BoxScene {...brp} showFaceLabels={false} zoom={0.7} />
						<ContentFooter
							RightButton={RightButton}
							LeftButton={LeftButton}
							confirmAction={ConfirmProps}
						/>
					</div>

					<Scroll className="full-size border-left">
						<div className="flex-col align-center full-size">
							<div className="full-width-minus-2pad flex-col align-start double-margin-top double-margin-bottom">
								<span className="bold-font margin-bottom">Slip Sheet Name</span>
								<input
									className="full-width"
									type="text"
									value={slipsheet.name}
									onChange={lNameHandlers.Event}
									onClick={lTriggerKeyboardName}
								/>
							</div>
							<CoordinateItem
								pPromptItem="slip sheet"
								name={'Height'}
								axis_str="Z"
								value={slipsheet.dimensions.height}
								units_system={unitsSystem}
								setter={updateDimension('height')}
								triggerKeyboard={triggerKeyboard}
							/>
							{/* SS type */}
							<div className="full-width-minus-2pad flex-row align-start double-margin-top double-margin-bottom">
								<input
									className="container-border"
									style={{ height: '20px', width: '20px' }}
									type="checkbox"
									checked={slipsheet.rigid || false}
									onChange={event => {
										updateRigid(event.target.checked);
									}}
								/>
								<span className="bold-font margin-left">Rigid</span>
							</div>
						</div>
					</Scroll>
				</div>
			</div>
		</ContentItem>
	);
}

function getDefaultSS(lCurNumSS: number): SlipSheet_t {
	return {
		id: String(uuidv4()),
		name: 'Slip sheet ' + String(lCurNumSS + 1),
		dimensions: { length: 500, width: 500, height: 0 },
	};
}

enum SlipSheetSteps {
	Dimensions,
	Summary,
}

type SlipSheetSetter = ConfigSetter<SlipSheet_t[]>;

interface SSHook {
	Step: UseStateObject<SlipSheetSteps>;
	EditingSS: UseStateObject<SlipSheet_t>;
	EditingIndex: UseStateObject<number | null>;
	StartEditSS: (pIndex: number) => void;
	LeftButton: ButtonProps;
	RightButton: ButtonProps;
	TriggerConfirm: ConfirmTrigger;
	ConfirmProps: ConfirmAction | undefined;
}

const useSS = (
	pAllSS: SlipSheet_t[],
	pSetSS: SlipSheetSetter,
	pHandleBack: () => void,
	pHandleNext: () => void,
	unitsSystem: UnitsSystem
): SSHook => {
	const [lCurrentStep, lSetCurrentStep] = useState<SlipSheetSteps>(
		SlipSheetSteps.Summary
	);
	const [lTriggerConfirm, lConfirmProps] = useConfirm([lCurrentStep]);
	const [lEditingSS, lSetEditingSS] = useState<SlipSheet_t>(
		getDefaultSS(Object.keys(pAllSS).length)
	);
	const [lEditingIndex, lSetEditingIndex] = useState<number | null>(null);
	const lStartEdit = function (pIndex: number) {
		if (pIndex >= 0) {
			// edit
			lSetEditingSS(pAllSS[pIndex]);
			lSetEditingIndex(pIndex);
		} else {
			// create new
			lSetEditingIndex(null);
			lSetEditingSS(getDefaultSS(Object.keys(pAllSS).length));
		}
		lSetCurrentStep(SlipSheetSteps.Dimensions);
	};
	const lLeftButton: ButtonProps = {
		name: lCurrentStep === SlipSheetSteps.Summary ? '' : 'Back',
		action: () => {
			switch (lCurrentStep) {
				case SlipSheetSteps.Summary:
					pHandleBack();
					break;
				case SlipSheetSteps.Dimensions:
					lSetCurrentStep(SlipSheetSteps.Summary);
					break;
			}
		},
		enabled: lCurrentStep !== SlipSheetSteps.Summary,
	};
	const didBoxChange = function () {
		if (lEditingIndex === null) return;
		const lOldSS = pAllSS[lEditingIndex];
		const lCompareDim = function (lDim: keyof BoxDimensions) {
			return lOldSS.dimensions[lDim] === lEditingSS.dimensions[lDim];
		};
		return !lCompareDim('length') || !lCompareDim('width');
	};
	const lRightButton: ButtonProps = {
		name: lCurrentStep === SlipSheetSteps.Summary ? 'Create a Pallet' : 'Save',
		action: () => {
			if (lCurrentStep === SlipSheetSteps.Summary) {
				if (Object.keys(pAllSS).length > 0) pHandleNext();
				return;
			}
			if (lEditingIndex !== null) {
				let ssc = JSON.parse(JSON.stringify(pAllSS));
				const lDidChange = didBoxChange();
				ssc[lEditingIndex] = {
					...lEditingSS,
					changed: lDidChange,
				};
				pSetSS(ssc);
				lSetCurrentStep(SlipSheetSteps.Summary);
				return;
			} else {
				const ssc = [...pAllSS, lEditingSS];
				pSetSS(ssc);
				lSetEditingIndex(ssc.length - 1);
			}
			// short circuit to summary after first step.
			lSetCurrentStep(SlipSheetSteps.Summary);
		},
		enabled: lCurrentStep === SlipSheetSteps.Summary ? pAllSS.length > 0 : true,
		hidden: false,
	};
	return {
		Step: { Current: lCurrentStep, Set: lSetCurrentStep },
		EditingSS: { Current: lEditingSS, Set: lSetEditingSS },
		EditingIndex: { Current: lEditingIndex, Set: lSetEditingIndex },
		StartEditSS: lStartEdit,
		LeftButton: lLeftButton,
		RightButton: lRightButton,
		TriggerConfirm: lTriggerConfirm,
		ConfirmProps: lConfirmProps,
	};
};

export interface SSConfigurationProps extends ConfigurationProps {}

export default function SlipSheetSize({
	allSlipSheets,
	instructionNumber,
	setSlipSheets,
	handleBack,
	handleNext,
	unitsSystem,
	triggerKeyboard,
	triggerSelect,
}: SSConfigurationProps) {
	const lSSHook = useSS(
		allSlipSheets,
		setSlipSheets,
		handleBack,
		handleNext,
		unitsSystem
	);

	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const app = useContext(ApplicationContext) as Application;

	const startEdit = (pIndex: number) => () => lSSHook.StartEditSS(pIndex);
	const editingSlipSheet = lSSHook.EditingSS.Current;
	const setEditingSlipSheet = lSSHook.EditingSS.Set;
	const removeSS = (pSSIndex: number) => () => {
		lSSHook.TriggerConfirm(() => {
			let cp = [...allSlipSheets];
			cp[pSSIndex].deleted = true;
			setSlipSheets(cp);
		}, `Are you sure you want to delete ${allSlipSheets[pSSIndex].name}?`);
	};
	const lItemIterator = useMemo(
		function (): ItemIterator {
			return {
				pGet: function (
					pStartIndex: number,
					pEndOffset: number,
					pSearchFilter: string | undefined
				) {
					let lSS = [...allSlipSheets].map(function (pSS, pSSIndex) {
						return [pSS, pSSIndex] as [SlipSheet_t, number];
					});
					if (pSearchFilter) {
						const lRegExp = new RegExp(pSearchFilter, 'i');
						lSS = lSS.filter(([pSS, pSSIndex]) => lRegExp.test(pSS.name));
					}
					const lItemCount = lSS.length;
					return [
						lSS
							.splice(pStartIndex, pEndOffset)
							.map(function (pSSPair: [SlipSheet_t, number], pRelativeIndex: number) {
								const lIndex = pSSPair[1],
									slipsheetCellProps: SlipSheetProps = {
										slipsheet: pSSPair[0],
										unitsSystem: unitsSystem,
										startEdit: startEdit(lIndex),
										handleDelete: removeSS(lIndex),
                    handleCopy: duplicateConfiguration(lIndex, allSlipSheets, setSlipSheets)
									};
								return <SlipSheetCell {...slipsheetCellProps} key={lIndex} />;
							}),
						lItemCount,
					];
				},
				triggerKeyboard: triggerKeyboard,
			};
		},
		[allSlipSheets]
	);

	let instruction: string;
	const sub_step_count = 5; // _What is this_?
	const sub_step = lSSHook.Step.Current + 1;
	switch (lSSHook.Step.Current) {
		case SlipSheetSteps.Dimensions:
			instruction = 'Enter Slip Sheet Dimensions';
			const createSSProps: CreateNewSlipSheetProps = {
				slipsheet: editingSlipSheet,
				unitsSystem: unitsSystem,
				LeftButton: lSSHook.LeftButton,
				RightButton: lSSHook.RightButton,
				updateSlipSheet: setEditingSlipSheet,
				instruction,
				instructionNumber,
				sub_step,
				sub_step_count,
				ConfirmProps: lSSHook.ConfirmProps,
				triggerKeyboard,
				triggerSelect,
			};

			return <CreateNewSlipSheet {...createSSProps} />;

		case SlipSheetSteps.Summary:
			instruction = 'Create And Edit Slip Sheets';
			const AddButton: ButtonProps = {
				name: 'Add new slip sheet',
				action: startEdit(-1),
			};
			const contentItemProps: ContentItemProps = {
				instruction,
				instructionNumber,
				LeftButton: lSSHook.LeftButton,
				RightButton: lSSHook.RightButton,
				AddButton,
				sub_step,
				sub_step_count,
				isSummary: true,
				confirmAction: lSSHook.ConfirmProps,
			};
			const summaryProps = {
				...lItemIterator,
				instruction,
				AddButton,
				RightFooterButton: lSSHook.RightButton,
				LeftFooterButton: lSSHook.LeftButton,
				ConfirmFooter: lSSHook.ConfirmProps,
			};
			return (
				<div className="full-size flex-center align-center">
					<SummaryScroll {...summaryProps} />
				</div>
			);
	}
}
